<template>
  <div v-if="modelValue" class="ag-note-modal-backdrop">
    <div class="ag-note-modal">
      <div class="ag-note-modal-header d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <i class="ag-icon ag-icon_blue_note" style="width:16px;height:16px;margin-right:8px;"></i>
          <strong style="color: #0055A6; font-size: 20px;">{{ title || $t('addNote') }}</strong>
        </div>
        <button class="btn btn-link p-0" @click="onCancel" aria-label="Close">
          <i class="pi pi-times" style="font-size: 1rem; color: #0055A6;"></i>
        </button>
      </div>
      <div class="ag-note-modal-body">
        <div class="ag-note-info" v-if="displayInfo && props.info !== 'Note'" v-html="displayInfo"></div>

        <textarea v-if="showTextarea" v-model="draft" class="notes-textarea" :placeholder="$t('enterNote')"
          :maxlength="maxlength" rows="6"
          style="resize: vertical; background-color: transparent; border: 1px solid #D2D9E2"></textarea>
        <div v-if="showTextarea" class="counter" style="text-align: end; font-weight: bold;">{{
          draft.length }}/{{ maxlength }} {{ $t('characters') }}</div>
      </div>
      <div class="ag-note-modal-footer d-flex justify-content-end gap-2">
        <button class="btn btn-cancel" style="background: transparent !important; color: #8e8e8e !important;
          border: 2px solid #b1b1b1 !important;
          box-shadow: none !important;" @click="onCancel">{{ $t('cancel') }}</button>
        <button class="btn" style="background-color: #164b6c; color: #fff; padding-left: 30px; padding-right: 30px;"
          @click="onOk">{{ computedConfirmText }}</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits, computed } from 'vue'
import { useI18n } from 'vue-i18n';
const { t } = useI18n();
const props = defineProps({
  modelValue: { type: Boolean, default: false },
  note: { type: String, default: '' },
  title: { type: String, default: '' },
  maxlength: { type: Number, default: 1000 },
  action: { type: String, default: 'apply' }, // 'apply' | 'delete' | 'reset'
  count: { type: Number, default: 0 },
  info: { type: String, default: '' },
  confirmText: { type: String, default: '' },
  showTextarea: { type: Boolean, default: false },
  requireNote: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue', 'update:note', 'save', 'cancel'])

const draft = ref('')

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      draft.value = props.note || ''
    }
  },
  { immediate: true }
)

const pluralS = computed(() => (props.count === 1 ? '' : 's'))

const defaultInfoByAction = computed(() => {
  const n = props.count
  if (props.action === 'delete') {
    return `${n} ${t('item')}${pluralS.value} ${t('willBeDeleted')}<br/><br/>${t('thisActionCannotBeUndone')} ${t('pleaseConfirmYouWantToProceed')}`
  }
  if (props.action === 'reset') {
    return `${n} ${t('item')}${pluralS.value} ${t('willBeReset')}<br/><br/>${t('thisWillOverwriteCurrentSettingsForTheSelectedAppGroup')}`
  }
  return `${n} ${t('item')}${pluralS.value} ${t('willBeUpdated')}<br/><br/>${t('pleaseReviewBeforeApplyingChanges')}`
})

const displayInfo = computed(() => props.info && props.info.length ? props.info : defaultInfoByAction.value)

const computedConfirmText = computed(() => {
  if (props.confirmText) return props.confirmText
  if (props.action === 'delete') return t('delete')
  if (props.action === 'reset') return t('reset')
  return t('applyChanges')
})

function onCancel() {
  emit('cancel')
  emit('update:modelValue', false)
}
function onOk() {
  emit('update:note', draft.value)
  emit('save', draft.value)
  emit('update:modelValue', false)
}
</script>

<style scoped>
.ag-note-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ag-note-modal {
  background: #fff;
  width: 545px;
  border: 1px solid #D9D9D9;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  border-radius: 4px;
}

.ag-note-modal-header {
  padding: 12px 30px;
  border-bottom: 1px solid #E5E7EB;
}

.ag-note-modal-body {
  padding: 30px;
  background-color: #F5F5F5;
}

.ag-note-info {
  color: #3D4B5A;
  font-size: 14px;
  margin-bottom: 14px;
}

.ag-note-modal-footer {
  padding: 20px 30px 20px 30px;
  border-top: 1px solid #D5D5D5;
  background-color: #F5F5F5;
}
.notes-textarea {
    width: 100%;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    padding: 8px;
    font-size: 0.8rem;
    resize: vertical;
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 transparent;
}

.notes-textarea:focus,
.notes-textarea:focus-visible {
    outline: none;
    box-shadow: none;
    border-color: #cbd5e1;
}

.notes-textarea::-webkit-scrollbar {
    width: 6px;
    height: 6px;
}
.notes-textarea::-webkit-scrollbar-track {
    background: transparent;
}
.notes-textarea::-webkit-scrollbar-thumb {
    background-color: #cbd5e1; 
    border-radius: 8px;
    border: 2px solid transparent;
    background-clip: content-box;
}
.notes-textarea::-webkit-scrollbar-thumb:hover {
    background-color: #94a3b8;
}
.counter {
    text-align: right;
    color: #6b7280;
    font-size: 0.75rem;
    margin-top: 4px;
}
</style>